import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import {
  AssignAnyProjectAndUserToJob,
  CreateJob as CreateJobPermission,
} from "../../../services/authorization/Permissions";
import PageLayout from "../../_layout/PageLayout";
import { JobInput } from "../../../api-models/request/JobInput";
import {
  EditJobName,
  EditProject,
  EditJobStatus,
  EditJobType,
  EditPriority,
} from "../components/DataComponents";
import EditAssignedUser from "../../../components/data-components/EditAssignedUser";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import Form from "../../../components/form/Form";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import { FormikHelpers, FormikValues } from "formik";
import { HistoryLocationProjectState } from "../../projects/project-jobs/ProjectJobsPage";
import { dateService } from "../../../services/date-service/DateService";
import { JobsStorageModel } from "../../../services/job/JobsStorage";
import { EditAvailableFromAndDeadlineDates } from "../../projects/components/DataComponents";
import { api } from "../../../services/api/ApiService";
import {
  DataSection,
  EditDescriptionSection,
  SubSection,
} from "../../../components/data-components/sections/DataSections";
import { JobMenuItemsModel } from "../JobMenuItems";
import { warningLeavePageService } from "../../../services/page/WarningLeavePageService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { currentUserService } from "../../../services/user/CurrentUserStorage";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { JobPriority, JobStatus, JobType } from "../../../generated/Jobs";

export default observer(() => {
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();

  const [jobsStorage] = useState(() => JobsStorageModel.create({ autoLoad: false }, { api }));

  const [jobMenuItems] = useState(() => JobMenuItemsModel.create({ createMode: true }));

  const locationState: HistoryLocationProjectState = history.location
    .state as HistoryLocationProjectState;

  const menuItems = {
    staticItems: jobMenuItems.getStaticItems(),
  };

  const initialValues = useMemo(() => {
    return {
      name: "",
      project: locationState?.project,
      status: JobStatus.ToDo,
      type: JobType.DataGathering,
      assignedUser: !permissionsService.userHasPermissionTo(AssignAnyProjectAndUserToJob)
        ? {
            id: currentUserService.currentUser.id,
            name: currentUserService.currentUser.name,
          }
        : null,
      availableFrom: dateService.currentDate,
      deadline: null,
      priority: JobPriority.Medium,
      description: "",
    };
  }, [locationState]);

  const goToJobsList = useCallback(() => history.push("/jobs"), [history]);

  const goToProject = useCallback(
    (projectId: number) => history.push(`/projects/${projectId}/jobs`),
    [history]
  );

  const redirect = useCallback(() => {
    if (locationState?.project) return goToProject(locationState.project.id as number);
    return goToJobsList();
  }, [goToJobsList, goToProject, locationState]);

  const handleSubmit = useCallback(
    async (values: FormikValues, formActions: FormikHelpers<FormikValues>) => {
      const availableFrom = dateService.dateToUTC(values.availableFrom);

      let deadline: any = null;
      if (values.deadline) deadline = dateService.dateToUTC(values.deadline);

      const job: JobInput = {
        name: values.name,
        projectId: values.project.id,
        status: Number(values.status),
        type: Number(values.type),
        assignedUserId: values.assignedUser ? values.assignedUser.id : null,
        timeline: {
          availableFrom: availableFrom,
          deadline: deadline,
        },
        priority: Number(values.priority),
        description: values.description,
      };

      try {
        await jobsStorage.createJob(job);
        warningLeavePageService.setIsDirty(false);
        redirect();
      } catch (error) {
        const applicationError = error as ApplicationError;
        applicationError.withForm(values, formActions);
        applicationError.withErrorMessage("default-error.job.create");
        handleErrorInSnackbar(applicationError);
      }
    },
    [handleErrorInSnackbar, jobsStorage, redirect]
  );

  useEffect(() => {
    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
  }, [history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[CreateJobPermission]}
      titleKey="CreateJob.add"
      menuItems={menuItems}
    >
      <Form
        formikOptions={{
          initialValues: initialValues,
          onSubmit: handleSubmit,
          validateOnBlur: false,
          validateOnChange: false,
        }}
        onCancel={redirect}
      >
        {({ values, setFieldValue }) => {
          return (
            <>
              <DataSection labelKey="general.jobs.details">
                <SubSection>
                  <EditJobName name={values.name} />
                  <EditProject project={values.project} setFieldValue={setFieldValue} />
                  <EditJobStatus status={values.status} setFieldValue={setFieldValue} />
                </SubSection>
                <SubSection>
                  <EditJobType type={values.type} setFieldValue={setFieldValue} />
                  <EditAssignedUser
                    assignedUser={values.assignedUser}
                    setFieldValue={setFieldValue}
                    disabled={!permissionsService.userHasPermissionTo(AssignAnyProjectAndUserToJob)}
                  />
                  <EditPriority priority={values.priority} setFieldValue={setFieldValue} />
                </SubSection>
                <SubSection>
                  <EditAvailableFromAndDeadlineDates
                    availableFrom={values.availableFrom}
                    deadline={values.deadline}
                    setFieldValue={setFieldValue}
                  />
                </SubSection>
                <SubSection>
                  <DisplayDateCreated />
                  <DisplayCreatedBy />
                </SubSection>
              </DataSection>
              <EditDescriptionSection description={values.description} />
            </>
          );
        }}
      </Form>
    </PageLayout>
  );
});
